import { buildElationUrlDraftPrescription } from '@seen/core/elation'
import {
  type DraftPharmacy,
  draftPharmacyFromMedication,
} from '@seen/core/elationDraftPharmacy'
import { featureFlags } from '@seen/core/featureFlags'
import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Participant } from '@seen/core/models/patients/participant'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { useConfig } from '#src/contexts/config/useConfig.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import {
  classifyCreateDraftFailureReason,
  humanizeCreateDraftErrorMessage,
  type MedicationRefillSurface,
  reportMedicationRefillDraftCreated,
  reportMedicationRefillDraftFailed,
  reportMedicationRefillDraftRequested,
  reportMedicationRefillSignOpened,
} from '#src/hooks/medicationRefillObservability.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { getMedicationThreadId } from '#src/hooks/useMedicationDraftsByThread.ts'
import { useOptimisticDraftStore } from '#src/hooks/useOptimisticDraftStore.ts'
import { useRead } from '#src/hooks/useRead.ts'

type DraftOrderType = 'Refill'

interface DraftPrescriptionResponse {
  draftId: number
  elationPatientId: number
  pharmacy?: {
    ncpdpid?: string | null
    storeName?: string | null
    searchText?: string | null
  }
}

/* The create response has the ZIP-qualified search text. Sign can fire on
 * the same row before the Medplum refetch returns the stamped draft, and the
 * source medication does not have that search text. Keep the response here
 * until then. After the draft is loaded, previousDispenser is the source. */
const rememberedDraftPharmacy = new Map<string, DraftPharmacy>()

export function clearRememberedDraftPharmacies(): void {
  rememberedDraftPharmacy.clear()
}

export interface UseElationDraftPrescriptionResult {
  createDraftPrescription: (
    medication: ExtendedMedicationRequest,
    orderType: DraftOrderType
  ) => Promise<number | undefined>
  openDraftPrescription: (
    draftId: number | string,
    medication?: ExtendedMedicationRequest
  ) => void
  isMedicationDrafting: (medicationId: string | undefined) => boolean
  draftingMedicationIds: ReadonlySet<string>
  optimisticDraftIdsByThreadId: ReadonlyMap<string, number | string>
  isDrafting: boolean
  canDraft: boolean
  isLoading: boolean
}

// Hook to handle draft prescription flow (Refill / DoseChange):
// 1. POST /elation/prescribe to create draft in Elation
// 2. Keep per-medication pending state in a global zustand store
//    so all consumers (medications page, sidebar card, visit notes)
//    share the same optimistic state and it survives navigation.
export function useElationDraftPrescription(
  patientId: string | undefined,
  options?: { surface?: MedicationRefillSurface }
): UseElationDraftPrescriptionResult {
  const queryClient = useQueryClient()
  const { elationBaseUrl } = useConfig()
  const { seenApi } = useSeenApi()
  const { data: participant, isLoading } = useRead(Participant, patientId)
  const {
    draftIdsByThreadId: optimisticDraftIdsByThreadId,
    draftingMedicationIds,
    setDraft,
    addDraftingMedication,
    removeDraftingMedication,
  } = useOptimisticDraftStore()

  const elationPatientId = participant?.elationId
  const canDraft = elationPatientId !== undefined
  const surface = options?.surface
  const defaultPreviousPharmacy = useFeatureFlag(
    featureFlags.setRefillDraftPharmacy
  )
  const openUnsignedDraft = useFeatureFlag(
    featureFlags.openUnsignedElationDraft
  )

  const openDraftPrescription = useCallback(
    (draftId: number | string, medication?: ExtendedMedicationRequest) => {
      if (elationPatientId === undefined) {
        toast.error('Patient not linked to Elation')
        reportMedicationRefillDraftFailed({
          surface,
          medicationRequestId: medication?.id,
          reason: 'no_elation_patient',
        })
        return
      }

      reportMedicationRefillSignOpened({
        surface,
        medicationRequestId: medication?.id,
        draftId,
      })
      const pharmacy = defaultPreviousPharmacy
        ? (rememberedDraftPharmacy.get(String(draftId)) ??
          (medication ? draftPharmacyFromMedication(medication) : undefined))
        : undefined
      const url = buildElationUrlDraftPrescription({
        elationBaseUrl,
        elationPatientId,
        draftId,
        pharmacy,
        openUnsignedDraft,
      })
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    [
      defaultPreviousPharmacy,
      elationBaseUrl,
      elationPatientId,
      openUnsignedDraft,
      surface,
    ]
  )

  const createDraftPrescription = useCallback(
    async (
      medication: ExtendedMedicationRequest,
      orderType: DraftOrderType
    ): Promise<number | undefined> => {
      const medicationLabel = medication.medicationDisplay ?? 'Medication'
      const medicationRequestId = medication.id

      if (!patientId || elationPatientId === undefined) {
        toast.error(`${medicationLabel}: patient is not linked to Elation`)
        reportMedicationRefillDraftFailed({
          surface,
          medicationRequestId,
          reason: 'no_elation_patient',
        })
        return undefined
      }

      if (!medicationRequestId) {
        toast.error(`${medicationLabel}: medication is missing an ID`)
        reportMedicationRefillDraftFailed({
          surface,
          reason: 'missing_medication_id',
        })
        return undefined
      }

      if (!medication.externalResourceJSON) {
        toast.error(
          `${medicationLabel}: medication cannot be refilled (not Elation-sourced)`
        )
        reportMedicationRefillDraftFailed({
          surface,
          medicationRequestId,
          reason: 'not_elation_sourced',
        })
        return undefined
      }

      if (draftingMedicationIds.has(medicationRequestId)) {
        return undefined
      }

      addDraftingMedication(medicationRequestId)

      try {
        reportMedicationRefillDraftRequested({
          surface,
          medicationRequestId,
          orderType,
        })
        const response = await seenApi.post<DraftPrescriptionResponse>(
          '/elation/prescribe',
          {
            patientReference: `Patient/${patientId}`,
            medicationRequestId,
            orderType,
            surface,
            includePreviousPharmacy: defaultPreviousPharmacy,
          }
        )

        const { draftId } = response.data
        const returned = response.data.pharmacy
        const ncpdpid = returned?.ncpdpid?.trim()
        const storeName = returned?.storeName?.trim()
        if (ncpdpid && storeName) {
          rememberedDraftPharmacy.set(String(draftId), {
            ncpdpid,
            storeName,
            searchText: returned?.searchText?.trim() || storeName,
          })
        }
        const threadId = getMedicationThreadId(medication)

        if (threadId) {
          setDraft(threadId, draftId)
        }

        /* Ensure draft state refreshes from Medplum after create */
        void queryClient.invalidateQueries({
          queryKey: ['MedicationRequest', 'search'],
        })
        void queryClient.invalidateQueries({
          queryKey: ['MedicationRequest', 'searchExtended'],
        })

        const label = orderType === 'Refill' ? 'Refill' : 'Dose change'
        toast.success(
          `${medicationLabel}: ${label.toLowerCase()} draft created`
        )
        reportMedicationRefillDraftCreated({
          surface,
          medicationRequestId,
          draftId,
        })

        return draftId
      } catch (error) {
        const axiosMessage = (
          error as { response?: { data?: { message?: string } } }
        )?.response?.data?.message
        const rawMessage =
          axiosMessage ??
          (error instanceof Error ? error.message : 'Unknown error')

        const message = humanizeCreateDraftErrorMessage(rawMessage)
        toast.error(`${medicationLabel}: refill draft failed - ${message}`)
        reportMedicationRefillDraftFailed({
          surface,
          medicationRequestId,
          reason: classifyCreateDraftFailureReason(rawMessage),
        })

        return undefined
      } finally {
        removeDraftingMedication(medicationRequestId)
      }
    },
    [
      addDraftingMedication,
      draftingMedicationIds,
      elationPatientId,
      defaultPreviousPharmacy,
      patientId,
      queryClient,
      removeDraftingMedication,
      seenApi,
      setDraft,
      surface,
    ]
  )

  const isMedicationDrafting = useCallback(
    (medicationId: string | undefined): boolean => {
      if (!medicationId) {
        return false
      }
      return draftingMedicationIds.has(medicationId)
    },
    [draftingMedicationIds]
  )

  const isDrafting = draftingMedicationIds.size > 0

  return {
    createDraftPrescription,
    openDraftPrescription,
    isMedicationDrafting,
    draftingMedicationIds,
    optimisticDraftIdsByThreadId,
    isDrafting,
    canDraft,
    isLoading,
  }
}
